import { generalizeUrl } from "./urlGeneralize";

const UUID = "[a-f\\d]{8}-[a-f\\d]{4}-[a-f\\d]{4}-[a-f\\d]{4}-[a-f\\d]{12}";
const identifierStart = "(^|[^a-z\\d])";
const identifierEnd = "(?![a-z\\d])";
const uploadId = new RegExp(`${identifierStart}\\d{4}_\\d{2}_\\d{2}_${UUID}${identifierEnd}`, "gi");
const attachmentId = new RegExp(
	`${identifierStart}\\d{10,}-${UUID}(?=\\.pdf${identifierEnd})`,
	"gi"
);
const uuid = new RegExp(`${identifierStart}${UUID}${identifierEnd}`, "gi");

const generalizeIdentifiers = (message: string): string =>
	message
		.replace(uploadId, "$1{id}")
		.replace(attachmentId, "$1{id}")
		.replace(uuid, "$1{id}")
		.replace(/\{id\}(?:,\{id\})+/g, "{id}");

const generalizeResourceUrl = (url: string): string =>
	generalizeUrl(generalizeIdentifiers(url), ["version"]);

type MessageRule = {
	pattern: RegExp;
	message: (match: RegExpMatchArray) => string;
};

const messageRules: Record<string, MessageRule> = {
	eventPayload: {
		pattern: /^(Could not post event with eventName: [^\r\n]+?) with data:[\s\S]*$/,
		message: match => match[1],
	},
	malformedTimestamp: {
		pattern: /^Malformed timestamp:[\s\S]*$/,
		message: () => "Malformed timestamp",
	},
	uploadCompletion: {
		pattern: /^Error completing upload\. completeDoc:[\s\S]*$/,
		message: () => "Error completing upload",
	},
	dynamicImport: {
		pattern:
			/^((?:Failed to load component [^\r\n]+?\. Reason: )?(?:(?:TypeError|Error): )?Failed to fetch dynamically imported module):\s*\S[\s\S]*$/,
		message: match => match[1],
	},
	firefoxDynamicImport: {
		pattern: /^(error loading dynamically imported module): https?:\/\/\S+$/,
		message: match => match[1],
	},
	communitySearch: {
		pattern:
			/^((?:(?:TypeError|Error): )?(?:Request failed with status code \d+|Network Error) for )(\/api\/5\/bulbs\/community\?\S*)$/,
		message: match =>
			match[1] + generalizeResourceUrl(match[2].replace(/([?&]name=)[^&]*/g, "$1{id}")),
	},
	vimeoVideo: {
		pattern:
			/^([“"]https:\/\/vimeo\.com\/video\/)\d+(\/[”"] (?:was not found|is not embeddable)\.)$/,
		message: match => `${match[1]}{id}${match[2]}`,
	},
	monotonicTimestamp: {
		pattern:
			/^(Timestamps must be monotonically increasing) \(went from -?\d+(?:\.\d+)? to -?\d+(?:\.\d+)?\)\.$/,
		message: match => match[1],
	},
	uploadItem: {
		pattern:
			/^(\[Uploads\] Unrecognized (?:media|content) item \(id: )\d+(, mimeType: [^\r\n]+\) — item will be excluded)$/,
		message: match => `${match[1]}{id}${match[2]}`,
	},
	uppyTimestamp: {
		pattern: /^(\[Uppy\] )\[\d{2}:\d{2}:\d{2}\]( [^\r\n]+)$/,
		message: match => `${match[1]}[{id}]${match[2]}`,
	},
	boxParserTimestamp: {
		pattern: /^\[\d+:\d{2}:\d{2}\.\d+\]( \[BoxParser\] [^\r\n]+)$/,
		message: match => `[{id}]${match[1]}`,
	},
	apiLoop: {
		pattern: /^(API_CALL_LOOP_)((?:https?:\/\/[^/\s]+)?\/\S*)$/,
		message: match => match[1] + generalizeResourceUrl(match[2]),
	},
	httpResource: {
		pattern:
			/^((?:(?:TypeError|Error): )?(?:Request failed with status code \d+|Network Error) for )((?:https?:\/\/[^/\s]+)?\/\S*)$/,
		message: match => match[1] + generalizeResourceUrl(match[2]),
	},
	vimeoResource: {
		pattern: /^([“"]https:\/\/vimeo\.com\/)\d+\/[a-zA-Z\d]+([”"] was not found\.)$/,
		message: match => `${match[1]}{id}/{id}${match[2]}`,
	},
	jsonLocation: {
		pattern:
			/^((?:Expected |Unexpected )[^\r\n]*JSON) at position \d+(?: \(line \d+ column \d+\))?$/,
		message: match => match[1],
	},
	firefoxJsonLocation: {
		pattern: /^(JSON\.parse: [^\r\n]+) at line \d+ column \d+ of the JSON data$/,
		message: match => match[1],
	},
	jsonExcerpt: {
		pattern: /^(Unexpected token '[^']+'),\s*"[\s\S]*"\.\.\. is not valid JSON$/,
		message: match => `${match[1]}: invalid JSON`,
	},
	videoDuration: {
		pattern:
			/^(Seconds must be a positive number less than the duration of the video) \(\d+(?:\.\d+)?\)\.$/,
		message: match => `${match[1]}.`,
	},
	cueNumber: {
		pattern: /^(End must be greater than start) \(cue #\d+\)$/,
		message: match => match[1],
	},
	queueCount: {
		pattern: /^(RTC_COMMANDS_QUEUE_STUCK)_\d+$/,
		message: match => match[1],
	},
	hlsPlaylist: {
		pattern: /^(VIDEOJS: ERROR: \(CODE:\d+ [^)]+\) HLS playlist request error) at URL: [\s\S]+$/,
		message: match => match[1],
	},
	videoJsObject: {
		pattern: /^(VIDEOJS: ERROR: [^\r\n]+?) \[object [a-zA-Z_$][\w$]*\]$/,
		message: match => match[1],
	},
	messageObject: {
		pattern:
			/^(Unable to process (?:the YouTube URL|WeVideo selection|selected video)\.|Unable to load Kaltura video\.|Error initializing playlist:|Uppy upload error:|Error handling bulb completion message:|Canvas Studio request failed\.) \[object [a-zA-Z_$][\w$]*\]$/,
		message: match => match[1].replace(/:$/, ""),
	},
	pluginUrl: {
		pattern: /^(Failed to load plugin: [^\r\n]+?) from url [\s\S]+$/,
		message: match => match[1],
	},
	cssUrl: {
		pattern: /^(Unable to preload CSS) for [\s\S]+$/,
		message: match => match[1],
	},
	popupUrl: {
		pattern: /^(\[GSI_LOGGER\]: Failed to open popup window) on url: [\s\S]+$/,
		message: match => match[1],
	},
	socketId: {
		pattern: /^(Trying to push on socket )socket_\d+( of type: [^\r\n]+, but we are offline!)$/,
		message: match => `${match[1]}{id}${match[2]}`,
	},
	objectLookupId: {
		pattern:
			/^(Non-Error promise rejection captured with value: Object Not Found Matching Id:)\d+(, MethodName:[^,\r\n]+, ParamCount:\d+)$/,
		message: match => `${match[1]}{id}${match[2]}`,
	},
	regexInput: {
		pattern:
			/^Invalid regular expression: \/[^\r\n]*\/[a-z]*: (Unterminated group|Unterminated character class)$/,
		message: match => `Invalid regular expression: ${match[1]}`,
	},
	cueMalformed: {
		pattern: /^Cue malformed: start timestamp greater than end[\s\S]*$/,
		message: () => "Cue malformed: start timestamp greater than end",
	},
	cueOrder: {
		pattern: /^Cue number \d+ is not in chronological order$/,
		message: () => "Cue number {id} is not in chronological order",
	},
	videoAppend: {
		pattern:
			/^(VIDEOJS: ERROR: \(CODE:\d+ [^)]+\)) video append of \d+b failed for segment #\d+ in playlist [\s\S]+$/,
		message: match => `${match[1]} video append failed`,
	},
	classAccess: {
		pattern: /^User \d+ doesn't have access to class \d+$/,
		message: () => "User {id} doesn't have access to class {id}",
	},
	classNotFound: {
		pattern: /^Couldn't find class with id \d+ for user \d+$/,
		message: () => "Couldn't find class with id {id} for user {id}",
	},
	bulbPermission: {
		pattern:
			/^(User is not allowed to access bulb |User doesn't have ASSIGN permissions on bulb )\d+$/,
		message: match => `${match[1]}{id}`,
	},
	folderInvite: {
		pattern: /^(Invalid invite code for folder with ID )\d+$/,
		message: match => `${match[1]}{id}`,
	},
	binarySearch: {
		pattern: /^(Failed binary finding record in array - , searched for )-?\d+$/,
		message: match => `${match[1]}{id}`,
	},
	rangeOffset: {
		pattern:
			/^(Failed to execute 'setStart' on 'Range': The offset )\d+( is larger than the node's length \()\d+(\)\.)$/,
		message: match => `${match[1]}{id}${match[2]}{id}${match[3]}`,
	},
	volumeRange: {
		pattern:
			/^(Failed to set the 'volume' property on 'HTMLMediaElement': The volume provided )\(\d+(?:\.\d+)?\)( is outside the range \[0, 1\]\.)$/,
		message: match => `${match[1]}({id})${match[2]}`,
	},
	playInterrupted: {
		pattern:
			/^(?:AbortError: )?(The play\(\) request was interrupted [^\r\n]+?\.)(?: https:\/\/goo\.gl\/\S+)?$/,
		message: match => match[1],
	},
	shaderInfoLog: {
		pattern:
			/^(THREE\.WebGLProgram: Shader Error \d+ - VALIDATE_STATUS false Program Info Log:)[\s\S]*$/,
		message: match => match[1],
	},
	correlatedError: {
		pattern: /^([^\r\n]+?) The error may be correlated with this previous error:[\s\S]*$/,
		message: match => match[1],
	},
	imageDimensions: {
		pattern: /^Failed to get image dimensions for: [\s\S]+$/,
		message: () => "Failed to get image dimensions",
	},
	urlNotWorking: {
		pattern: /^(\[\d+\]) \S+ Not working$/,
		message: match => `${match[1]} {url} Not working`,
	},
};

export const generalizeMessage = (message: string): string => {
	for (const rule of Object.values(messageRules)) {
		const match = message.match(rule.pattern);
		if (match) return generalizeIdentifiers(rule.message(match));
	}
	return generalizeIdentifiers(message);
};
